import { cachedFetchStore, flushCacheAndHardReload } from '../app/localStorage';
import { APP_VERSION } from '@/config/constants';
import { Mutex } from 'async-mutex';
import bugsnagClient from '@/ui/containers/BugsnagClient';
import chunk from 'lodash/chunk';
import merge from 'lodash/merge';
import objectHash from 'object-hash';
import throttle from 'lodash/throttle';

export const ENDPOINT = `${window.location.origin}/api2`;
export const DEFAULT_MAX_API_RESULT_COUNT = 200;

// caching
const CACHE_TIMEOUT_MINUTES = 7 * 24 * 60;
const THROTTLED_FLUSH_CACHE_WAIT = 60 * 1000;
const LOCK_QUEUE = {};
const LAST_REQUEST_TIME_KEY = 'LAST_REQUEST_TIME_KEY';

// TODO: move meta state update refresh to background worker?
const DELTA_STATE_UPDATE_SECONDS = 30 * 60;

const getOrCreateLock = (key) => {
    const lock = LOCK_QUEUE[key];

    if (lock != null) {
        return lock;
    }

    LOCK_QUEUE[key] = new Mutex();
    return LOCK_QUEUE[key];
};

// TODO: skip cache based on url parameter
// TODO: clear cache based on url parameter
export const cachedFetch = (fn) => {
    const response = (...args) => {
        if (!('data' in args[0])) return fn(...args);

        // TODO: look at using Auth0/JWT for the hash when the args are JSON
        const key = objectHash([args[0].data, args[0].domain]);
        const lock = getOrCreateLock(key);
        // block all repeated calls for the same args until the first one
        // returns, then return cached value

        return lock.runExclusive(async () => {
            try {
                const lastRequestTime = await cachedFetchStore.getItem(
                    LAST_REQUEST_TIME_KEY,
                );
                const existingResult = await cachedFetchStore.getItem(key);
                const timeSinceLastRequest =
                    lastRequestTime !== null
                        ? (lastRequestTime - Date.now()) / 1000
                        : null;

                if (
                    existingResult !== null &&
                    timeSinceLastRequest !== null &&
                    timeSinceLastRequest < DELTA_STATE_UPDATE_SECONDS
                ) {
                    return existingResult;
                }
            } catch (err) {
                bugsnagClient.notify(err);
            }

            const value = await fn(...args);
            // TODO: look at using last_updated field for timeout calculation, when the field is
            // available
            // TODO: look at compressing/gzip value before storing it
            if (value === undefined || value === null) return value;

            try {
                await Promise.all([
                    cachedFetchStore.setItem(key, value),
                    cachedFetchStore.setItem(LAST_REQUEST_TIME_KEY, Date.now()),
                ]);
            } catch (err) {
                bugsnagClient.notify(err);
                // TODO: only clear if the error is low space
                cachedFetchStore.clear();
            } finally {
                return value;
            }
        });
    };
    return response;
};

export const paginator = (
    fn,
    { chunkSize } = { chunkSize: DEFAULT_MAX_API_RESULT_COUNT },
) => {
    // TODO: fix promises
    const paginatedFn = async (...args) => {
        if (!fn) {
            throw `Missing function to paginate: ${fn}`;
        }
        const argsDict = { ...{ paginateBy: 'ids' }, ...args[0] };
        const { paginateBy } = argsDict;

        const data = 'data' in argsDict ? argsDict.data : args[0];

        if (!(paginateBy in data)) {
            throw `Data badly formatted? Unable to find pagination key
            ${paginateBy} in ${Object.keys(data)}`;
        }

        const errors = [];
        if (Object.keys(data).length < 1) {
            const result = fn(...args).catch((err) => {
                errors.push(data);
                console.log(
                    'Paginated element failed - ',
                    data,
                    ' - with errors',
                    err,
                );
            });
            // TODO: return errors too + update state
            return result;
        }

        const idsToPaginate = data[paginateBy].sort();
        const chunks = chunk(idsToPaginate, chunkSize);

        const chunkedRequests = chunks.map((chunk) => {
            const dataTemplate = merge({}, data);
            dataTemplate[paginateBy] = chunk;
            return [fn({ ...args[0], ...{ data: dataTemplate } }), chunk];
        });

        let results = await Promise.all(
            chunkedRequests.map((r) => {
                return r[0].catch((err) => {
                    console.error('fetch error for', r[1], ' - ', err);
                    console.log(err.name, err.message);
                    errors.push({ error: err, ids: r[1] });
                    return Promise.resolve(undefined);
                });
            }),
        );
        results = results.filter((e) => e).flat();

        return { results, ...(errors.length && { errors }) };
    };
    return paginatedFn;
};

const flushCacheIfNeccessary = (response) => {
    const currentUpdate = response.headers.get('last_updated');
    const serverAppVersion = response.headers.get('app_version');
    if (currentUpdate == null) return Promise.resolve();

    const lastUpdatedKey = 'LAST_UPDATED';
    const lock = getOrCreateLock(lastUpdatedKey);
    return lock.runExclusive(async () => {
        const lastUpdate = await cachedFetchStore.getItem(lastUpdatedKey);
        if (APP_VERSION != serverAppVersion) return flushCacheAndHardReload();
        if (!window?.spate_env?.[lastUpdatedKey]) {
            try {
                window.spate_env[lastUpdatedKey] = currentUpdate;
            } catch (err) {
                bugsnagClient.notify(err);
            }
        } else if (window?.spate_env?.[lastUpdatedKey] !== currentUpdate) {
            return flushCacheAndHardReload();
        }
        if (currentUpdate != lastUpdate)
            return Promise.all([
                cachedFetchStore.clear(),
                cachedFetchStore.setItem(lastUpdatedKey, currentUpdate),
            ]);
    });
};

export const throttledFlushIfNeccessary = throttle(
    flushCacheIfNeccessary,
    // don't flush cache more frequently than once a minute
    THROTTLED_FLUSH_CACHE_WAIT,
    { trailing: false },
);

export const endpointWrapper = async ({
    domain,
    data,
    params,
    controller = null,
    method = 'POST',
}) => {
    // TODO: validate domain
    const args = {
        method,
        body: JSON.stringify(data),
    };

    if (controller) {
        args.signal = controller.signal;
    }

    // Query strings go here rather than in `domain`: the trailing slash is part
    // of the route, so it would land after the query value.
    const query = params ? `?${new URLSearchParams(params)}` : '';
    const response = await fetch(`${ENDPOINT}/${domain}/${query}`, args);

    if (response.ok) {
        throttledFlushIfNeccessary(response).catch((err) => {
            bugsnagClient.notify(err);
        });
        const text = await response.text();
        return text && JSON.parse(text);
    }
    console.error('Response not ok!', data, response.text, response);
    const err = new Error(`Response not okay!`);
    err.status = response.status;
    return Promise.reject(err);
};

export class ChunkedRequestError extends Error {
    constructor(errors) {
        const message = `Failed to fetch ${errors.reduce((total, { ids }) => total + ids.length, 0)} items`;
        super(message);
        this.name = 'ChunkedRequestError';
        this.message = message;
        this.message = errors.map(({ error }) => error.message);
    }
}
